import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { BackButton } from "../../components/BackButton";
import { resetPassword } from "../../api/auth";
import { ApiError } from "../../api/client";

const MIN_PASSWORD = 6;

/**
 * Forgot-password step 3 — set a new password after OTP verification.
 *
 * The OTP has been verified and the code travels here in navigation state.
 * This screen spends it against `POST /api/auth/reset-password` along with
 * the new password, in one call — same pattern as registration.
 */
export function NewPasswordScreen() {
  const navigate = useNavigate();
  const { email = "", code = "" } = (useLocation().state ?? {}) as {
    email?: string;
    code?: string;
  };

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const passwordError =
    submitted && password.length < MIN_PASSWORD
      ? `Needs at least ${MIN_PASSWORD} characters.`
      : null;
  const confirmError =
    submitted && confirm.length > 0 && password !== confirm ? "Passwords don't match." : null;

  const ready = password.length >= MIN_PASSWORD && password === confirm;

  const submit = async () => {
    setSubmitted(true);
    if (!ready || saving) return;

    setSaving(true);
    setFailure(null);

    try {
      await resetPassword(email, code, password);
      navigate("/login", { state: { resetSuccess: true } });
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : "Couldn't reset your password. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/login" />

      <h1 className="m-0 pt-4 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.2]">
        Set a new password
      </h1>
      <p className="m-0 pt-2 text-[13.5px] text-text-muted">
        Pick something you'll remember — at least {MIN_PASSWORD} characters.
      </p>

      <div className="flex flex-col gap-4 pt-6">
        <Field
          label="New password"
          htmlFor="new-pw"
          error={passwordError}
          hint={`At least ${MIN_PASSWORD} characters`}
        >
          <div
            className={`${inputShell} justify-between ${passwordError ? "border-negative" : "border-border-strong"}`}
          >
            <input
              id="new-pw"
              type={revealed ? "text" : "password"}
              autoComplete="new-password"
              aria-invalid={Boolean(passwordError)}
              className="w-full bg-transparent text-[15px] text-text outline-none"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              onClick={() => setRevealed((shown) => !shown)}
              aria-label={revealed ? "Hide password" : "Show password"}
              className="-m-2 shrink-0 p-2 text-text-muted"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </button>
          </div>
        </Field>

        <Field label="Confirm password" htmlFor="confirm-pw" error={confirmError}>
          <div
            className={`${inputShell} ${confirmError ? "border-negative" : "border-border-strong"}`}
          >
            <input
              id="confirm-pw"
              type="password"
              autoComplete="new-password"
              aria-invalid={Boolean(confirmError)}
              className="w-full bg-transparent text-[15px] text-text outline-none"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </div>
        </Field>
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-8">
        {failure ? (
          <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
            {failure}
          </p>
        ) : null}
        <Button onClick={() => void submit()} disabled={(submitted && !ready) || saving}>
          {saving ? "Setting password…" : "Set new password"}
        </Button>
      </div>
    </div>
  );
}
